import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { Progress } from "@/components/ui/progress";
import { Switch } from "@/components/ui/switch";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { cn } from "@/lib/utils";
import { UserCog } from "lucide-react";
import { useCreateOrganization } from "@/hooks/useOrganizations";
import { AppBrandingEditor, appBrandingColourErrors } from "@/components/corporate/organizations/AppBrandingEditor";
import {
  AppPackageFields,
  ATHLYN_APP_PACKAGE,
  appPackageErrors,
  selectedPackage,
  type AppPackageValue,
} from "@/components/corporate/organizations/AppPackageFields";
import type { CreateOrganizationRequest } from "@/types/organizations";
import { COUNTRY_OPTIONS, DEFAULT_COUNTRY, LANGUAGE_OPTIONS } from "@/components/corporate/organizations/orgOptions";

interface AddOrganizationWizardProps {
  isOpen: boolean;
  onClose: () => void;
}

// The create request, except the external-users choice is tri-state in the form: `null` until the
// operator explicitly picks one (no default — owner decision D1), and required before Next.
type OrganizationFormData = Omit<CreateOrganizationRequest, "allowExternalUsers"> & {
  allowExternalUsers: boolean | null;
};

/** The app fields start on the Athlyn app (owner Q7). */
const initialAppPackage = (): AppPackageValue => ({ choice: ATHLYN_APP_PACKAGE, other: "", appStoreId: "" });

/** One source for the initial state AND the reset in handleClose. */
function initialFormData(): OrganizationFormData {
  return {
    // Required fields per CMS API
    name: "",
    subdomain: "",
    defaultLanguage: "en",
    adminEmail: "",
    adminFirstName: "",
    adminLastName: "",
    locationName: "",
    departmentName: "",
    // Sign-in & access — externals: explicit choice required; perks + health onboarding + chat: on by default
    allowExternalUsers: null,
    perksEnabled: true,
    healthOnboardingEnabled: true,
    chatEnabled: true,

    // Optional fields per CMS API
    primaryColor: "#0EA5E9",
    secondaryColor: "#0369A1",
    taxRegistrationNumber: "",
    address: "",
    postalCode: "",
    state: "",
    city: "",
    // One country list, stored as ISO codes (B11).
    country: DEFAULT_COUNTRY,
    adminPhoneNumber: "",
    tenantId: "",
    accentColor: "",
    employeeAppBrandingEnabled: false
  };
}


export default function AddOrganizationWizard({ isOpen, onClose }: AddOrganizationWizardProps) {
  const { toast } = useToast();
  const createOrganization = useCreateOrganization();
  const [currentStep, setCurrentStep] = useState(1);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const [formData, setFormData] = useState<OrganizationFormData>(initialFormData);
  const [appPackage, setAppPackage] = useState<AppPackageValue>(initialAppPackage);
  // App branding is for companies on the Athlyn app — the select's "Athlyn" option.
  const usesAthlynApp = appPackage.choice === ATHLYN_APP_PACKAGE;


  // Use the hook-based mutation instead

  const validateStep = (step: number): boolean => {
    const newErrors: Record<string, string> = {};

    switch (step) {
      case 1:
        // Required fields for trial API
        if (!formData.name.trim()) newErrors.name = "Organization name is required";
        if (!formData.subdomain.trim()) newErrors.subdomain = "Subdomain is required";
        else if (!/^[a-zA-Z0-9][a-zA-Z0-9-]*[a-zA-Z0-9]$/.test(formData.subdomain)) {
          newErrors.subdomain = "Subdomain must be alphanumeric with hyphens, cannot start/end with hyphen";
        }
        if (!formData.adminEmail.trim()) newErrors.adminEmail = "Admin email is required";
        else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.adminEmail)) {
          newErrors.adminEmail = "Invalid email format";
        }
        if (!formData.adminFirstName.trim()) newErrors.adminFirstName = "Admin first name is required";
        if (!formData.adminLastName.trim()) newErrors.adminLastName = "Admin last name is required";
        if (!formData.locationName.trim()) newErrors.locationName = "Location name is required";
        if (!formData.departmentName.trim()) newErrors.departmentName = "Department name is required";
        // Optional field validation
        if (formData.adminPhoneNumber && !/^[\+]?[0-9\s\-\(\)]+$/.test(formData.adminPhoneNumber)) {
          newErrors.adminPhoneNumber = "Invalid phone number format";
        }
        if (formData.allowExternalUsers === null) {
          newErrors.allowExternalUsers = "Choose whether external (guest) users are allowed";
        }
        Object.assign(newErrors, appPackageErrors(appPackage));
        break;
      case 2: {
        // Colours must be #RRGGBB (or empty) — the API rejects anything else with a 400.
        // Inline messages are rendered by AppBrandingEditor itself.
        const colourErrors = appBrandingColourErrors({
          primaryColor: formData.primaryColor,
          secondaryColor: formData.secondaryColor,
          accentColor: formData.accentColor,
        });
        Object.assign(newErrors, colourErrors);
        break;
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => prev + 1);
    }
  };

  const handleBack = () => {
    setCurrentStep(prev => prev - 1);
  };

  const handleClose = () => {
    setCurrentStep(1);
    setErrors({});
    setFormData(initialFormData());
    setAppPackage(initialAppPackage());
    onClose();
  };

  const handleCreate = () => {
    if (validateStep(currentStep)) {
      // Prepare the data to send - convert empty strings to null for optional fields
      // Step 1 validation guarantees the choice was made; never default it here.
      if (formData.allowExternalUsers === null) return;
      const dataToSend: CreateOrganizationRequest = {
        ...formData,
        allowExternalUsers: formData.allowExternalUsers,
        perksEnabled: formData.perksEnabled ?? true,
        healthOnboardingEnabled: formData.healthOnboardingEnabled ?? true,
        chatEnabled: formData.chatEnabled ?? true,
        adminPhoneNumber: formData.adminPhoneNumber?.trim() || null,
        tenantId: formData.tenantId?.trim() || null,
        taxRegistrationNumber: formData.taxRegistrationNumber?.trim() || null,
        address: formData.address?.trim() || null,
        postalCode: formData.postalCode?.trim() || null,
        state: formData.state?.trim() || null,
        city: formData.city?.trim() || null,
        country: formData.country?.trim() || null,
        primaryColor: formData.primaryColor?.trim() || null,
        secondaryColor: formData.secondaryColor?.trim() || null,
        accentColor: formData.accentColor?.trim() || null,
        // Only the Athlyn app takes app branding — never send the switch on for another app.
        employeeAppBrandingEnabled: usesAthlynApp ? (formData.employeeAppBrandingEnabled ?? false) : false,
        // "Athlyn" sends no package: the API applies its environment's default (owner r5-Q1).
        appScheme: usesAthlynApp ? null : selectedPackage(appPackage),
        appStoreId: appPackage.appStoreId.trim() || null,
      };

      createOrganization.mutate(dataToSend, {
        onSuccess: () => {
          handleClose();
        }
      });
    }
  };

  // Auto-generate subdomain from name
  const handleNameChange = (value: string) => {
    const subdomain = value.toLowerCase().replace(/[^a-z0-9]/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '');
    setFormData(prev => ({
      ...prev,
      name: value,
      subdomain: subdomain
    }));
  };



  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-4">
            <div>
              <Label htmlFor="client-name">Organization Name *</Label>
              <Input
                id="client-name"
                value={formData.name}
                onChange={(e) => handleNameChange(e.target.value)}
                placeholder="Enter organization name"
                aria-invalid={!!errors.name}
                aria-describedby={errors.name ? "name-error" : undefined}
              />
              {errors.name && (
                <p id="name-error" className="text-sm text-destructive mt-1">{errors.name}</p>
              )}
            </div>

            <div>
              <Label htmlFor="subdomain">Website URL *</Label>
              <div className="relative">
                <Input
                  id="subdomain"
                  value={formData.subdomain ? `${formData.subdomain}.portal.athlyn.app` : ''}
                  onChange={(e) => {
                    // Extract subdomain from full URL input
                    const value = e.target.value;
                    const subdomain = value.replace('.portal.athlyn.app', '').replace(/[^a-z0-9-]/g, '');
                    setFormData(prev => ({ ...prev, subdomain }));
                  }}
                  placeholder="organization-name.portal.athlyn.app"
                  aria-invalid={!!errors.subdomain}
                  aria-describedby={errors.subdomain ? "subdomain-error" : undefined}
                  className="pl-24"
                />
                <div className="absolute left-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground bg-background px-1">
                  https://
                </div>
              </div>
              {errors.subdomain && (
                <p id="subdomain-error" className="text-sm text-destructive mt-1">{errors.subdomain}</p>
              )}
              <p className="text-xs text-muted-foreground mt-1">
                This will be the organization's unique website address
              </p>
            </div>

            {/* Admin Information */}
            <div className="space-y-4">
              <div className="border-t pt-4">
                <h3 className="text-lg font-medium mb-3 flex items-center gap-2">
                  <UserCog className="h-5 w-5" />
                  Primary Administrator
                </h3>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="admin-first-name">First Name *</Label>
                    <Input
                      id="admin-first-name"
                      value={formData.adminFirstName}
                      onChange={(e) => setFormData(prev => ({ ...prev, adminFirstName: e.target.value }))}
                      placeholder="John"
                      aria-invalid={!!errors.adminFirstName}
                      aria-describedby={errors.adminFirstName ? "admin-first-name-error" : undefined}
                    />
                    {errors.adminFirstName && (
                      <p id="admin-first-name-error" className="text-sm text-destructive mt-1">{errors.adminFirstName}</p>
                    )}
                  </div>

                  <div>
                    <Label htmlFor="admin-last-name">Last Name *</Label>
                    <Input
                      id="admin-last-name"
                      value={formData.adminLastName}
                      onChange={(e) => setFormData(prev => ({ ...prev, adminLastName: e.target.value }))}
                      placeholder="Doe"
                      aria-invalid={!!errors.adminLastName}
                      aria-describedby={errors.adminLastName ? "admin-last-name-error" : undefined}
                    />
                    {errors.adminLastName && (
                      <p id="admin-last-name-error" className="text-sm text-destructive mt-1">{errors.adminLastName}</p>
                    )}
                  </div>
                </div>

                <div>
                  <Label htmlFor="admin-email">Admin Email *</Label>
                  <Input
                    id="admin-email"
                    type="email"
                    value={formData.adminEmail}
                    onChange={(e) => setFormData(prev => ({ ...prev, adminEmail: e.target.value }))}
                    placeholder="admin@organization.com"
                    aria-invalid={!!errors.adminEmail}
                    aria-describedby={errors.adminEmail ? "admin-email-error" : undefined}
                  />
                  {errors.adminEmail && (
                    <p id="admin-email-error" className="text-sm text-destructive mt-1">{errors.adminEmail}</p>
                  )}
                  <p className="text-xs text-muted-foreground mt-1">
                    This person will be the primary administrator for the organization
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="admin-phone">Phone Number</Label>
                    <Input
                      id="admin-phone"
                      type="tel"
                      value={formData.adminPhoneNumber || ''}
                      onChange={(e) => setFormData(prev => ({ ...prev, adminPhoneNumber: e.target.value }))}
                      placeholder="+971 50 123 4567"
                      aria-invalid={!!errors.adminPhoneNumber}
                      aria-describedby={errors.adminPhoneNumber ? "admin-phone-error" : undefined}
                    />
                    {errors.adminPhoneNumber && (
                      <p id="admin-phone-error" className="text-sm text-destructive mt-1">{errors.adminPhoneNumber}</p>
                    )}
                  </div>
                  <div>
                    <Label htmlFor="tenant-id">Entra Tenant ID</Label>
                    <Input
                      id="tenant-id"
                      value={formData.tenantId || ''}
                      onChange={(e) => setFormData(prev => ({ ...prev, tenantId: e.target.value }))}
                      placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                      aria-invalid={!!errors.tenantId}
                      aria-describedby={errors.tenantId ? "tenant-id-error" : undefined}
                    />
                    {errors.tenantId && (
                      <p id="tenant-id-error" className="text-sm text-destructive mt-1">{errors.tenantId}</p>
                    )}
                    <p className="text-xs text-muted-foreground mt-1">
                      Optional: Microsoft Entra tenant ID for SSO integration
                    </p>
                  </div>
                </div>
              </div>

            </div>

            <AppPackageFields
              idPrefix="create-org"
              value={appPackage}
              onChange={(value) => {
                setAppPackage(value);
                setErrors(prev => {
                  const { appScheme: _scheme, appStoreId: _storeId, ...rest } = prev;
                  return rest;
                });
              }}
              errors={{ appScheme: errors.appScheme, appStoreId: errors.appStoreId }}
            />

            <div className="border-t pt-4 space-y-4" data-testid="create-org-access">
              <h3 className="text-lg font-medium">Sign-in &amp; access</h3>

              <div className="space-y-2">
                <Label id="allow-external-users-label">Allow external (guest) users? *</Label>
                <RadioGroup
                  aria-labelledby="allow-external-users-label"
                  aria-invalid={!!errors.allowExternalUsers}
                  aria-describedby={errors.allowExternalUsers ? "allow-external-users-error" : undefined}
                  value={formData.allowExternalUsers === null ? "" : formData.allowExternalUsers ? "yes" : "no"}
                  onValueChange={(value) => {
                    setFormData(prev => ({ ...prev, allowExternalUsers: value === "yes" }));
                    setErrors(prev => {
                      const { allowExternalUsers: _cleared, ...rest } = prev;
                      return rest;
                    });
                  }}
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="yes" id="allow-external-users-yes" />
                    <Label htmlFor="allow-external-users-yes" className="font-normal">
                      Yes — allow external (guest) users
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="no" id="allow-external-users-no" />
                    <Label htmlFor="allow-external-users-no" className="font-normal">
                      No — employees only
                    </Label>
                  </div>
                </RadioGroup>
                {errors.allowExternalUsers && (
                  <p id="allow-external-users-error" className="text-sm text-destructive mt-1">{errors.allowExternalUsers}</p>
                )}
                <p className="text-xs text-muted-foreground">
                  When not allowed, the app hides guest sign-in and the client portal disables Public visibility.
                </p>
              </div>

              <div className="flex items-center justify-between rounded-lg border p-4">
                <div className="space-y-0.5">
                  <Label htmlFor="perks-enabled">Perks enabled</Label>
                  <p className="text-xs text-muted-foreground">
                    When off, perks are hidden everywhere for this organization
                  </p>
                </div>
                <Switch
                  id="perks-enabled"
                  checked={formData.perksEnabled ?? true}
                  onCheckedChange={(checked) => setFormData(prev => ({ ...prev, perksEnabled: checked }))}
                />
              </div>

              <div className="flex items-center justify-between rounded-lg border p-4">
                <div className="space-y-0.5">
                  <Label htmlFor="health-onboarding-enabled">Health onboarding</Label>
                  <p className="text-xs text-muted-foreground">
                    When off, the app only asks for health when someone joins a challenge that needs it
                  </p>
                </div>
                <Switch
                  id="health-onboarding-enabled"
                  checked={formData.healthOnboardingEnabled ?? true}
                  onCheckedChange={(checked) => setFormData(prev => ({ ...prev, healthOnboardingEnabled: checked }))}
                />
              </div>

              <div className="flex items-center justify-between rounded-lg border p-4">
                <div className="space-y-0.5">
                  <Label htmlFor="chat-enabled">Chat</Label>
                  <p className="text-xs text-muted-foreground">
                    When off, employees of this organization have no chat
                  </p>
                </div>
                <Switch
                  id="chat-enabled"
                  checked={formData.chatEnabled ?? true}
                  onCheckedChange={(checked) => setFormData(prev => ({ ...prev, chatEnabled: checked }))}
                />
              </div>
            </div>

            <div className="border-t pt-4 space-y-4">
              <h3 className="text-lg font-medium">Initial Setup</h3>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="location-name">Location Name *</Label>
                  <Input
                    id="location-name"
                    value={formData.locationName}
                    onChange={(e) => setFormData(prev => ({ ...prev, locationName: e.target.value }))}
                    placeholder="e.g., Main Office, Headquarters"
                    aria-invalid={!!errors.locationName}
                    aria-describedby={errors.locationName ? "location-name-error" : undefined}
                  />
                  {errors.locationName && (
                    <p id="location-name-error" className="text-sm text-destructive mt-1">{errors.locationName}</p>
                  )}
                  <p className="text-xs text-muted-foreground mt-1">
                    The location for admin
                  </p>
                </div>

                <div>
                  <Label htmlFor="department-name">Department Name *</Label>
                  <Input
                    id="department-name"
                    value={formData.departmentName}
                    onChange={(e) => setFormData(prev => ({ ...prev, departmentName: e.target.value }))}
                    placeholder="e.g., General, Operations"
                    aria-invalid={!!errors.departmentName}
                    aria-describedby={errors.departmentName ? "department-name-error" : undefined}
                  />
                  {errors.departmentName && (
                    <p id="department-name-error" className="text-sm text-destructive mt-1">{errors.departmentName}</p>
                  )}
                  <p className="text-xs text-muted-foreground mt-1">
                    The department for admin
                  </p>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="country">Country</Label>
                <Select
                  value={formData.country ?? ""}
                  onValueChange={(value) => setFormData(prev => ({ ...prev, country: value }))}
                >
                  <SelectTrigger id="country">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {COUNTRY_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.country && (
                  <p className="text-sm text-destructive mt-1">{errors.country}</p>
                )}
              </div>

              <div>
                <Label htmlFor="language">Default Language</Label>
                <Select
                  value={formData.defaultLanguage}
                  onValueChange={(value) => setFormData(prev => ({ ...prev, defaultLanguage: value }))}
                >
                  <SelectTrigger id="language">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {LANGUAGE_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.defaultLanguage && (
                  <p className="text-sm text-destructive mt-1">{errors.defaultLanguage}</p>
                )}
              </div>
            </div>
          </div>
        );


      case 2:
        return (
          <div className="space-y-4">
            <Label className="text-base font-semibold">Brand Customization</Label>
            <AppBrandingEditor
              idPrefix="create-org"
              primaryColor={formData.primaryColor ?? ""}
              secondaryColor={formData.secondaryColor ?? ""}
              accentColor={formData.accentColor ?? ""}
              employeeAppBrandingEnabled={formData.employeeAppBrandingEnabled ?? false}
              usesAthlynApp={usesAthlynApp}
              onColourChange={(field, value) => setFormData(prev => ({ ...prev, [field]: value }))}
              onEnabledChange={(enabled) => setFormData(prev => ({ ...prev, employeeAppBrandingEnabled: enabled }))}
              organizationName={formData.name}
            />
          </div>
        );

    }
  };

  return (
    <>
      <Dialog open={isOpen} onOpenChange={handleClose}>
        <DialogContent className={cn("max-h-[90vh] overflow-hidden flex flex-col", currentStep === 2 ? "max-w-5xl" : "max-w-2xl")}>
          <DialogHeader>
            <DialogTitle>Create Trial Organization</DialogTitle>
            <div className="mt-4">
              <Progress value={(currentStep / 2) * 100} className="h-2" />
              <div className="flex justify-between mt-2 text-sm text-muted-foreground">
                <span className={currentStep === 1 ? "text-foreground font-medium" : ""}>
                  Organization Details
                </span>
                <span className={currentStep === 2 ? "text-foreground font-medium" : ""}>
                  Branding
                </span>
              </div>
            </div>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto px-6 py-4">
            {renderStepContent()}
          </div>

          <div className="flex justify-between items-center p-6 border-t">
            <Button type="button" variant="ghost" onClick={handleClose}>
              Cancel
            </Button>
            <div className="flex gap-2">
              {currentStep > 1 && (
                <Button type="button" variant="outline" onClick={handleBack}>
                  Back
                </Button>
              )}
              {currentStep < 2 ? (
                <Button type="button" onClick={handleNext}>
                  Next
                </Button>
              ) : (
                <Button
                  type="button"
                  onClick={handleCreate}
                  disabled={createOrganization.isPending}
                >
                  {createOrganization.isPending ? "Creating Trial..." : "Create Trial Organization"}
                </Button>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

    </>
  );
}